import React, { RefCallback } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { TextAreaWithRef } from "@/components/forms/inputsWithRef/textAreaWithRef";
import CrudFieldController from "@/components/tables/fields/CrudFieldController";
import {
  isInputBlockedByRule,
  jsonTextRule,
  patternRule,
  requiredRule,
} from "@/components/tables/fields/fieldRules";
import { textAreaFieldStyle } from "@/components/tables/fields/fieldStyles";
import { composeInputRef } from "@/components/tables/fields/validationTarget";
import type { CrudModalField } from "@/components/tables/fields/crudModalField";
import styles from "@/components/tables/fields/crudField.module.css";

export interface CrudJsonTextareaFieldProps {
  field: Pick<
    CrudModalField,
    | "id"
    | "header"
    | "required"
    | "errorMessage"
    | "rule"
    | "errorMessageValidation"
    | "textareaRows"
    | "hideField"
    | "blockInvalidInput"
  >;
  modalMode?: string;
  registerValidationTarget?: (name: string) => RefCallback<HTMLElement>;
  onChange?: (value: string) => void;
}

const toJsonText = (value: unknown) =>
  typeof value === "object" ? JSON.stringify(value, null, 2) : value;

const CrudJsonTextareaField = ({
  field,
  modalMode,
  registerValidationTarget,
  onChange,
}: CrudJsonTextareaFieldProps) => {
  const name = field.id as string;
  const { t } = useTranslation();
  const { control } = useFormContext();

  if (field.hideField) return null;

  return (
    <CrudFieldController
      name={name}
      control={control}
      rules={{
        ...requiredRule(field.required, field.errorMessage),
        ...patternRule(field, t),
        ...jsonTextRule(t),
      }}
      groupInputWithError
      render={({
        field: { onChange: setFormValue, value, ...rest },
        error,
      }) => {
        const text = toJsonText(value);
        if (!((modalMode === "edit" && text) || modalMode === "insert")) {
          return null; 
        }
        return (
          <TextAreaWithRef
            {...rest}
            ref={composeInputRef(
              rest.ref,
              registerValidationTarget?.(rest.name)
            )}
            label={field.header}
            required={field.required}
            name={field.id}
            onChange={(event: any) => {
              const typed = event.target.value;
              if (isInputBlockedByRule(field, typed)) return;
              onChange?.(typed);
              setFormValue(typed);
            }}
            value={text}
            wrapperClassname={`${styles.textField} ${styles.preWhiteSpace}`}
            rows={field.textareaRows ? field.textareaRows : 8}
            fieldStyle={textAreaFieldStyle(!!error)}
          />
        );
      }}
    />
  );
};

export default CrudJsonTextareaField;
